import { SkillMapDepth } from './skillGraphLayout';
import { SkillHaveFilter } from './skillGraphFilter';

/**
 * Состояние карты навыков в адресной строке (ТЗ 0070).
 *
 * До 0070 состояния экрана в адресе не было вовсе: выбранный навык жил в
 * `useState`, ссылку на конкретный узел дать было нельзя, обновление страницы
 * закрывало паспорт, а «назад» уводило с экрана.
 *
 * Правила собраны в двух ЧИСТЫХ функциях, потому что проверять пять параметров
 * руками в браузере — это то, что делают один раз, а ломают потом каждым
 * рефакторингом.
 */

export type SkillMapView = 'canvas' | 'tree' | null;

export interface ISkillMapParams {
  /** Профессия, чья карта показана. `null` — своя цель, канонический адрес. */
  track: string | null;
  /** Выбранный навык — по слагу, не по идентификатору узла. */
  skill: string | null;
  /** Код колонки, вокруг которой перестроен вид. Он же фильтр «группа». */
  focus: string | null;
  depth: SkillMapDepth;
  /** Отношение студента к навыку: пробелы, своё или всё. */
  have: SkillHaveFilter | null;
  /** Только навыки, без которых вакансия этой профессии не открывается. */
  core: boolean;
  /** Навыки, которые даёт конкретный курс школы. */
  course: string | null;
  deps: boolean;
  q: string;
  /** `null` — представление по умолчанию для этого экрана. */
  view: SkillMapView;
}

export const DEFAULTS = {
  depth: 2 as SkillMapDepth,
  deps: false,
};

/** Порядок ключей ФИКСИРОВАН: одно состояние — ровно один адрес. */
const KEY_ORDER = [
  'track',
  'skill',
  'focus',
  'depth',
  'have',
  'core',
  'course',
  'deps',
  'q',
  'view',
] as const;

/**
 * 🔴 Ключи, которые мы больше не понимаем, но обязаны вычищать. `mode` («мой
 * план» / «вся карта») удалён: он ничего не фильтровал ни одного дня. Просто
 * перестать его писать мало — чужие параметры адрес БЕРЕЖЁТ (см. ниже), и
 * `?mode=all` из старой закладки остался бы в строке навсегда.
 */
const LEGACY_KEYS = ['mode'] as const;

const SLUG = /^[a-z0-9][a-z0-9-]{0,63}$/;
/**
 * Идентификатор курса приходит с сервера и слагом не является: это base64
 * (`Q291cnNlOjc=`), поэтому в наборе есть и двоеточие, и знак равенства.
 */
const PARAM_ID = /^[A-Za-z0-9:_=-]{1,64}$/;

const readDepth = (raw: string | null): SkillMapDepth =>
  // Битое значение — не повод показать пустой экран. Глубин две: «только
  // группы» и «группы с навыками». Прежняя «3» отличалась от «2» лишь тем, что
  // включала колонку зависимостей, — этим теперь ведает отдельный ключ `deps`,
  // и старая ссылка `?depth=3` показывает ровно то же, что показывала.
  raw === '1' ? 1 : 2;

const readHave = (raw: string | null): SkillHaveFilter | null =>
  raw === 'gap' || raw === 'mine' ? raw : null;

export const readSkillMapParams = (search: URLSearchParams): ISkillMapParams => {
  const track = (search.get('track') || '').trim();
  const skill = (search.get('skill') || '').trim();
  const focus = (search.get('focus') || '').trim();
  const course = (search.get('course') || '').trim();
  const view = search.get('view');
  return {
    track: SLUG.test(track) ? track : null,
    skill: SLUG.test(skill) ? skill : null,
    focus: SLUG.test(focus) ? focus : null,
    depth: readDepth(search.get('depth')),
    have: readHave(search.get('have')),
    core: search.get('core') === '1',
    course: PARAM_ID.test(course) ? course : null,
    // Только явная «1» означает «да»: отсутствие параметра и `deps=0` — одно и
    // то же состояние, и второй записи в адресе оно не заслуживает.
    deps: search.get('deps') === '1',
    q: (search.get('q') || '').trim().slice(0, 64),
    view: view === 'tree' || view === 'canvas' ? view : null,
  };
};

/**
 * Следующий адрес.
 *
 * 🔴 Умолчания ВЫЧИЩАЮТСЯ. Одно состояние экрана обязано иметь ровно один
 * адрес — иначе два человека, сравнивающих ссылки на одну и ту же карту,
 * видят разные строки и ищут различие там, где его нет.
 *
 * 🔴 Чужие параметры (`utm_*` и прочие метки) СОХРАНЯЮТСЯ нетронутыми: нам
 * принадлежат десять своих ключей, а не вся строка запроса.
 *
 * Параметр, который в текущем режиме ни на что не влияет, в адрес не пишется:
 * `deps` на глубине 1 (узлов там нет вовсе), пустой поиск, неизвестная колонка.
 */
export const nextSkillMapSearch = (
  current: URLSearchParams,
  patch: Partial<ISkillMapParams>,
): URLSearchParams => {
  const merged = { ...readSkillMapParams(current), ...patch };
  const next = new URLSearchParams(current);

  const write = (key: string, value: string | null) => {
    if (value) next.set(key, value);
    else next.delete(key);
  };

  write('track', merged.track);
  write('skill', merged.skill);
  write('focus', merged.focus);
  write('depth', merged.depth === DEFAULTS.depth ? null : String(merged.depth));
  write('have', merged.have);
  write('core', merged.core ? '1' : null);
  write('course', merged.course);
  write('deps', merged.deps && merged.depth !== 1 ? '1' : null);
  write('q', merged.q || null);
  write('view', merged.view);
  LEGACY_KEYS.forEach((key) => next.delete(key));

  // Порядок ключей нормализуем в самом конце: `URLSearchParams` сохраняет
  // порядок вставки, и без этого один и тот же экран давал бы разные строки в
  // зависимости от того, что человек нажал раньше.
  const ordered = new URLSearchParams();
  KEY_ORDER.forEach((key) => {
    const value = next.get(key);
    if (value !== null) ordered.set(key, value);
  });
  next.forEach((value, key) => {
    if (!(KEY_ORDER as readonly string[]).includes(key)) ordered.set(key, value);
  });
  return ordered;
};

/**
 * Что попадает в историю, а что заменяет текущую запись.
 *
 * Выбор узла и фокус — `push`: ТЗ требует, чтобы «назад» возвращало предыдущий
 * выбранный навык, и тогда Escape становится симметричен клику, которым
 * паспорт открыли.
 *
 * Всё остальное — `replace`. Иначе «назад» протаскивает человека через
 * двенадцать нажатий на панели инструментов, прежде чем увести с экрана.
 */
export const isHistoryPush = (patch: Partial<ISkillMapParams>): boolean =>
  Object.prototype.hasOwnProperty.call(patch, 'skill') ||
  Object.prototype.hasOwnProperty.call(patch, 'focus') ||
  // Смена профессии — навигация: «назад» обязано вернуть на свою карту.
  Object.prototype.hasOwnProperty.call(patch, 'track');
